<script>
import { apiSecond } from '@/boot/axios'
import { numberFormaters } from '@/helpers'

import FishingYearSelector from '@/components/UI/FishingYearSelector.vue'
import HeaderBack from '@/components/UI/HeaderBack.vue'

// Цвет плашки закреплён за кодом вида рыболовства из handbook_fish_type_catches
// (4 — промысловое, 5 — любительское, 6 — научно-исследовательский лов,
// 7 — экспериментальный, 8 — мелиоративный, 9 — в воспроизводственных целях),
// чтобы вид узнавался по цвету, а не только по подписи.
const FISHING_TYPE_CHIPS = {
	4: 'bg-[#E2EEFB] text-[#1D4E8F]',
	5: 'bg-[#E9F6DF] text-[#3D6B21]',
	6: 'bg-[#EFE9FB] text-[#4B3187]',
	7: 'bg-[#E0F4F5] text-[#1E5C63]',
	8: 'bg-[#FCF0DC] text-[#8A5B12]',
	9: 'bg-[#FBE9EF] text-[#8A2347]',
}
const FISHING_TYPE_CHIP_DEFAULT = 'bg-[#F1F3F9] text-[#4B5363]'

export default {
	name: 'MyGears',
	components: {
		FishingYearSelector,
		HeaderBack,
	},
	data() {
		return {
			// null — данных ещё не было, страницу закрывает прелоадер;
			// [] — загрузились, но разрешений в периоде нет.
			rows: null,
			loading: false,
			period: this.$store.getters['dashboard/getSelectedFishingYear'],
			errorMessage: {
				status: null,
				text: null,
			},
		}
	},
	computed: {
		// Ответ плоский — строка на «вид рыболовства × тип орудия». Собираем карточки
		// по виду рыболовства, порядок строк из ответа сохраняем: бэк уже отдал нужный.
		groups() {
			if (!this.rows) return []

			const groups = []
			const indexByKey = new Map()

			this.rows.forEach((row) => {
				// Кода вида в разрешении может не быть — такие строки группируем по названию,
				// иначе все разрешения без кода слились бы в одну карточку.
				const key =
					row.fishing_type_code != null
						? `code:${row.fishing_type_code}`
						: `title:${row.fishing_type ?? ''}`

				if (!indexByKey.has(key)) {
					indexByKey.set(key, groups.length)
					groups.push({
						key,
						title: row.fishing_type || this.$t('gears.fishing_type_unknown'),
						chip: FISHING_TYPE_CHIPS[row.fishing_type_code] || FISHING_TYPE_CHIP_DEFAULT,
						items: [],
					})
				}

				groups[indexByKey.get(key)].items.push(row)
			})

			return groups
		},
	},
	watch: {
		// Виды рыболовства и названия орудий приходят локализованными с бэка,
		// а язык в шапке переключается без перезагрузки страницы — перезапрашиваем.
		'$i18n.locale'() {
			this.fetchGears()
		},
	},
	beforeCreate() {
		if (!localStorage.token) {
			this.$router.push('/ru/login')
		}
	},
	mounted() {
		this.fetchGears()
	},
	head: {
		title() {
			return {
				inner: this.$t('sidebar.my_gears'),
			}
		},
		meta: [],
	},
	methods: {
		// Строка уникальна внутри карточки: сопоставленные орудия — по id справочника,
		// нераспознанные — по сырому тексту eLicense, по которому их и группирует бэк.
		itemKey(item) {
			return item.gear_id != null ? `gear:${item.gear_id}` : `raw:${item.title}`
		},
		// count приходит числом: целое — int, дробное — float (из строк вида «2,5» в eLicense).
		formatCount(count) {
			if (count == null) return '—'
			return numberFormaters(count)
		},
		handlePeriodChange(period) {
			this.period = period
			// Период рыболовства общий для кабинета — держим его там же, где дашборд,
			// но без его перезагрузки: дашборд подтянет данные сам при заходе.
			this.$store.commit('dashboard/SET_SELECTED_FISHING_YEAR', period)
			this.fetchGears()
		},
		async fetchGears() {
			this.loading = true
			this.errorMessage = { status: null, text: null }

			try {
				const response = await apiSecond.get('v2/integrations/subject-gears', {
					// Разрешение попадает в сводку, если его срок пересекается с периодом.
					// Субъекту iin_bin передавать не нужно — бэк сам сжимает выборку до него.
					params: {
						date_from: this.period?.start,
						date_to: this.period?.finish,
					},
				})

				this.rows = Array.isArray(response?.data?.data) ? response.data.data : []
			} catch (error) {
				// rows не трогаем: первая загрузка — ошибку покажет прелоадер,
				// повторная — строки остаются на экране, а ошибка уходит в баннер.
				this.checkError(error)
			} finally {
				this.loading = false
			}
		},
		checkError(error) {
			const status = error?.response?.status

			if (status >= 500) {
				this.errorMessage.status = 500
				this.errorMessage.text = this.$t('system_message.500')
			} else if (status === 401) {
				this.errorMessage.status = 401
				this.errorMessage.text = error?.response?.data?.message
				this.$router.push('/' + this.$i18n.locale + '/login')
			} else {
				this.errorMessage.status = status || 422
				this.errorMessage.text = error?.response?.data?.message || this.$t('system_message.500')
			}
		},
	},
}
</script>

<template>
	<div
		class="wrap__content"
		v-bind:class="{ 'wrap__content--preloader': !$store.state.userObject || rows === null }"
	>
		<v-preloader
			v-if="!$store.state.userObject || rows === null"
			:message="errorMessage"
		></v-preloader>

		<div class="container" v-if="$store.state.userObject && rows !== null">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['MyGears']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<HeaderBack :title="$t('sidebar.my_gears')" />

							<div class="card__content--body">
								<div class="flex flex-wrap items-end justify-between gap-[16px] mb-[20px]">
									<div
										class="font-main font-normal text-[12px] text-second leading-[1.5] max-w-[560px]"
									>
										{{ $t('gears.subject_hint') }}
									</div>
									<FishingYearSelector
										:model-value="period"
										:title="$t('admin.hello.fishing_year')"
										:disabled="loading"
										@change="handlePeriodChange"
									/>
								</div>

								<div
									v-if="errorMessage.text"
									class="form__block--line form__messages form__messages--error"
								>
									<label class="form__block--input">
										<span class="form__block--title">
											{{ errorMessage.text }}
										</span>
									</label>
								</div>

								<div :class="{ 'opacity-50 pointer-events-none': loading }">
									<div v-for="group in groups" :key="group.key" class="mb-[24px] last:mb-0">
										<div
											class="inline-flex items-center font-main font-semibold text-[13px] leading-none px-[12px] py-[7px] rounded-[8px] mb-[10px]"
											:class="group.chip"
										>
											{{ group.title }}
										</div>

										<div class="border border-[#E3E7EF] rounded-[10px] overflow-hidden">
											<div class="overflow-x-auto">
												<table class="w-full border-collapse font-main text-[13px]">
													<thead>
														<tr class="bg-[#F7F9FC]">
															<th
																class="text-left font-semibold text-[#6B7280] px-[14px] py-[10px] border-b border-[#E3E7EF]"
															>
																{{ $t('gears.gear_type') }}
															</th>
															<th
																class="text-left font-semibold text-[#6B7280] px-[14px] py-[10px] border-b border-[#E3E7EF] w-[140px]"
															>
																{{ $t('gears.count') }}
															</th>
														</tr>
													</thead>
													<tbody>
														<tr
															v-for="item in group.items"
															:key="group.key + '|' + itemKey(item)"
															class="border-t border-[#EEF1F6] first:border-t-0"
														>
															<td class="px-[14px] py-[10px] align-top text-third">
																<div>{{ item.title || $t('gears.gear_type_unknown') }}</div>
																<div
																	v-if="!item.gear_id"
																	class="font-normal text-[11px] text-second leading-[1.4] mt-[3px]"
																>
																	{{ $t('gears.not_matched') }}
																</div>
																<div
																	v-if="item.unparsed"
																	class="font-normal text-[11px] text-[#8A5B12] leading-[1.4] mt-[3px]"
																>
																	{{ $t('gears.unparsed', [item.unparsed]) }}
																</div>
															</td>
															<td
																class="px-[14px] py-[10px] align-top font-semibold text-third whitespace-nowrap"
															>
																{{ formatCount(item.count) }}
															</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>
									</div>

									<div
										v-if="!groups.length"
										class="flex flex-col items-center justify-center py-[48px] text-center"
									>
										<div class="font-main font-medium text-[16px] text-fourth leading-[1.5]">
											{{ $t('gears.empty') }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
